import { memo } from "react";
import { CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const hours = (minute) => (minute >= 120 ? `${Math.round((minute / 60) * 10) / 10} h` : `${Math.round(minute)} min`);

/**
 * What the plan will do over time: chamber temperature and humidity (planned targets) and the water level.
 * points: [{minute, temp, rh, level}] from utils/planPreview.js. Memoised: only redraws when the plan changes.
 */
function PlanPreviewChart({ points, height = 220, title = "Plan preview" }) {
  const hasLevel = points.some((p) => typeof p.level === "number");
  if (!points.length) {
    return (
      <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-400">
        The plan preview appears here.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="plan-preview">
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">{title}</p>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 18, right: 4, bottom: 0, left: 4 }}>
            <CartesianGrid stroke="#f1f5f9" />
            <XAxis dataKey="minute" type="number" domain={[0, "dataMax"]} tickFormatter={hours} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="temp" tick={{ fontSize: 11, fill: "#ea580c" }} domain={[20, 60]} width={34}
              label={{ value: "°C", position: "top", offset: 6, fontSize: 11, fill: "#ea580c" }} />
            <YAxis yAxisId="rh" orientation="right" tick={{ fontSize: 11, fill: "#0e7490" }} domain={[20, 100]} width={34}
              label={{ value: "%RH", position: "top", offset: 6, fontSize: 11, fill: "#0e7490" }} />
            {hasLevel && (
              <YAxis yAxisId="level" orientation="right" tick={{ fontSize: 11, fill: "#2563eb" }} domain={[10, 45]} width={34}
                label={{ value: "cm", position: "top", offset: 6, fontSize: 11, fill: "#2563eb" }} />
            )}
            <Tooltip
              labelFormatter={(m) => `after ${hours(m)}`}
              formatter={(value, name) => [typeof value === "number" ? value.toFixed(1) : value, name]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="temp" dataKey="temp" name="Chamber temperature (°C)" stroke="#ea580c" dot={false} isAnimationActive={false} />
            <Line yAxisId="rh" dataKey="rh" name="Chamber humidity (%RH)" stroke="#0e7490" dot={false} isAnimationActive={false} />
            {hasLevel && (
              <Line yAxisId="level" dataKey="level" name="Water level (cm on the ruler)" stroke="#2563eb" strokeWidth={2} dot={false} type="stepAfter" connectNulls isAnimationActive={false} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default memo(PlanPreviewChart);
